import type { JSX } from 'preact';
import { useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { state, upsertEntity } from '../lib/store';
import {
  daysOf2026, formatReconDay, asReconDay, dailyOverUnder, batchOverUnder,
  selectedRange, dayInRange, sumMoneyCells, openReconDays, lastPaidBatchEnd,
  batchHasMoney,
} from '../lib/depositRecon';
import { saveDepositRecon, saveDepositBatch, softDeleteMoneyRow } from '../lib/moneyWrites';
import { refreshDepositRecon } from '../lib/bootstrap';
import { ApiError } from '../lib/apiClient';
import { uploadDepositBatchFile, deleteDepositBatchFile } from '../lib/depositReconFile';
import { useWrite } from './MoneyForms';
import { session } from '../lib/session';
import { withGoogleAccount } from './Nav';
import { LOCATION_NAMES, type LocationCode } from '../lib/locations';
import { CLINIC_TIME_ZONE } from '../lib/clinicDay';
import type { DepositBatchRow, DepositReconFile, DepositReconRow } from '../pages/Money';

const DAY_FIELDS = ['mr080', 'envelope', 'overUnder', 'notes'] as const;
type DayField = (typeof DAY_FIELDS)[number];

const DAY_LABEL: Record<DayField, string> = {
  mr080: 'MR080',
  envelope: '$ in Envelope',
  overUnder: 'Over/Under',
  notes: 'Notes',
};

function batchFiles(batch?: DepositBatchRow | null): DepositReconFile[] {
  if (!batch) return [];
  if (batch.files?.length) return batch.files;
  return [batch.receipt, batch.envelopeFile].filter((f): f is DepositReconFile => Boolean(f?.id));
}

function cellText(row: DepositReconRow | undefined, field: DayField): string {
  if (!row) return '';
  const v = row[field];
  return v === null || v === undefined ? '' : String(v);
}

function mergeDay(
  stored: DepositReconRow | undefined,
  draft: Partial<Record<DayField, string>>,
): Record<DayField, string> {
  const next = {
    mr080: cellText(stored, 'mr080'),
    envelope: cellText(stored, 'envelope'),
    overUnder: cellText(stored, 'overUnder'),
    notes: cellText(stored, 'notes'),
    ...draft,
  };
  if (draft.mr080 !== undefined || draft.envelope !== undefined) {
    const computed = dailyOverUnder(next.mr080, next.envelope);
    if (computed !== '') next.overUnder = computed;
  }
  return next;
}

function moneyLabel(raw: string | number | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return '—';
  const s = String(raw);
  if (s.startsWith('$') || s.startsWith('-$')) return s;
  return s.startsWith('-') ? `-$${s.slice(1)}` : `$${s}`;
}

function clinicName(locationCode: string): string {
  return LOCATION_NAMES[locationCode as LocationCode] ?? locationCode;
}

/** "2:05 PM" on the CLINIC's own clock (idea #23, owner ruling 2026-09-14).
 * Same convention as Masthead's clockOf and Tasks.tsx's formatClock — 12-hour,
 * no seconds, no timezone name shown (R-15 / INV-8) — but the hour and minute
 * themselves come from America/New_York (lib/clinicDay.ts), never the
 * reader's own browser zone. */
function formatClinicClock(now: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: CLINIC_TIME_ZONE, hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(now);
  return parts.replace(/ /g, ' ').toUpperCase();
}

/** N-093 (2026-09-10): the row-level delete control on a CLOSED batch is
 * owner/director only — a manager gets no control there at all (two
 * managers accidentally soft-deleted a closed batch this way). This mirrors
 * the server-side refusal in src/money.js's softDeleteMoneyRow exactly:
 * anything other than OWNER/DIRECTOR renders nothing. */
function canDeleteClosedBatch(role: string | null | undefined): boolean {
  return role === 'OWNER' || role === 'DIRECTOR';
}

function DayBoxes({
  day, stored, draft, onCommit, onType,
}: {
  day: string;
  stored: DepositReconRow | undefined;
  draft: Partial<Record<DayField, string>>;
  onCommit: (field: DayField, value: string, next: Record<DayField, string>) => void;
  // Idea #23: every keystroke is reported up too, so the grid-level Save
  // button (which lives outside this per-day component) always knows the
  // latest not-yet-committed values without changing what onBlur does below.
  onType: (day: string, next: Record<DayField, string>) => void;
}): JSX.Element {
  const [vals, setVals] = useState(() => mergeDay(stored, draft));

  function typeField(field: DayField, value: string): void {
    const next = mergeDay(undefined, { ...vals, [field]: value });
    setVals(next);
    onType(day, next);
  }

  return (
    <>
      {DAY_FIELDS.map((field) => (
        <td key={field}>
          <label class="deposit-day-field">
            <span>{DAY_LABEL[field]}</span>
            <input
              type="text"
              class="deposit-day-box"
              aria-label={`${DAY_LABEL[field]} ${formatReconDay(day)}`}
              data-testid={`deposit-recon-${field}-${day}`}
              value={vals[field]}
              onInput={(ev: Event) => typeField(field, (ev.target as HTMLInputElement).value)}
              onBlur={(ev: Event) => {
                const v = (ev.target as HTMLInputElement).value;
                onCommit(field, v, mergeDay(undefined, { ...vals, [field]: v }));
              }}
            />
          </label>
        </td>
      ))}
    </>
  );
}

export function DepositReconGrid({ locationCode }: { locationCode: string }): JSX.Element {
  const yearDays = useMemo(() => daysOf2026(), []);
  const reconTable = (state.value.depositRecon ?? {}) as Record<string, DepositReconRow>;
  const batchTable = (state.value.depositBatch ?? {}) as Record<string, DepositBatchRow>;
  const byDate = new Map<string, DepositReconRow>();
  for (const row of Object.values(reconTable)) {
    if (row.locationCode === locationCode) byDate.set(asReconDay(row.reconDate), row);
  }
  const batches = Object.values(batchTable)
    .filter((b) => b.locationCode === locationCode && !b.deletedAt)
    .sort((a, b) => asReconDay(a.dateStart).localeCompare(asReconDay(b.dateStart)) || a.id - b.id);
  const { busy, error, run } = useWrite();
  const [drafts, setDrafts] = useState<Record<string, Partial<Record<DayField, string>>>>({});
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [mr080Amount, setMr080Amount] = useState('');
  const [batchAmount, setBatchAmount] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [setNotes, setSetNotes] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const editingIdRef = useRef<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const role = session.value.me?.role;

  // Idea #23 (owner ruling 2026-09-14): a real Save button, on top of the
  // save-on-blur that already exists and is unchanged below.
  // `pendingByDay` holds only the days a box has been typed into where the
  // typed value still differs from what's actually saved — reported up by
  // DayBoxes' onType on every keystroke, cleared once that day is committed.
  const [pendingByDay, setPendingByDay] = useState<Record<string, Record<DayField, string>>>({});
  // How many day-row commits (blur OR the Save button — both run through
  // saveDay) are in flight right now. saveDay's own writes use
  // `{ freeze: false }` so they never touch useWrite's shared `busy`, which
  // is reserved for the batch/set actions — this is the day-grid's own.
  const [daySaveCount, setDaySaveCount] = useState(0);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Escape = Keep: closes the inline "Delete this batch" confirmation
  // without deleting anything, from anywhere on the page. useLayoutEffect
  // (not useEffect) so the listener is attached synchronously with the
  // render that shows the confirmation, rather than on the next animation
  // frame — otherwise an Escape sent immediately after opening it can land
  // before the listener exists.
  useLayoutEffect(() => {
    if (confirmDeleteId == null) return undefined;
    function onKeyDown(ev: KeyboardEvent): void {
      if (ev.key === 'Escape') setConfirmDeleteId(null);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [confirmDeleteId]);

  function setEditing(id: number | null): void {
    editingIdRef.current = id;
    setEditingId(id);
  }

  const lastClosedEnd = lastPaidBatchEnd(batches);
  const openDays = openReconDays(yearDays, lastClosedEnd);
  const editing = batches.find((b) => b.id === editingId) ?? null;
  const extraDays = editing
    ? yearDays.filter((d) => dayInRange(d, asReconDay(editing.dateStart), asReconDay(editing.dateEnd)))
    : [];
  const days = [...new Set([...extraDays, ...openDays])].sort();

  const selectedDays = days.filter((d) => picked[d]);
  const range = selectedRange(selectedDays);
  const setOverUnder = batchOverUnder(batchAmount, mr080Amount);
  const dailyEnvelopeTotal = range
    ? sumMoneyCells(days.filter((d) => dayInRange(d, range.start, range.end)).map((d) => drafts[d]?.envelope ?? cellText(byDate.get(d), 'envelope')))
    : '';
  const ytdDeposits = sumMoneyCells(yearDays.map((d) => drafts[d]?.envelope ?? cellText(byDate.get(d), 'envelope')));
  const dailyMr080Total = range
    ? sumMoneyCells(days.filter((d) => dayInRange(d, range.start, range.end)).map((d) => drafts[d]?.mr080 ?? cellText(byDate.get(d), 'mr080')))
    : '';

  function batchForDay(day: string): DepositBatchRow | undefined {
    return batches.find((b) => (
      dayInRange(day, asReconDay(b.dateStart), asReconDay(b.dateEnd))
      && (batchHasMoney(b.mr080Amount, b.batchAmount) || b.id === editingId)
    ));
  }

  function dailyTotalFor(start: string, end: string): string {
    return sumMoneyCells(
      yearDays.filter((d) => dayInRange(d, asReconDay(start), asReconDay(end))).map((d) => drafts[d]?.envelope ?? cellText(byDate.get(d), 'envelope')),
    );
  }

  function toggleDay(day: string): void {
    const owned = batchForDay(day);
    if (owned && owned.id !== editingId) {
      loadBatch(owned);
      return;
    }
    setPicked((cur) => ({ ...cur, [day]: !cur[day] }));
  }

  function loadBatch(batch: DepositBatchRow): void {
    const next: Record<string, boolean> = {};
    for (const day of yearDays) {
      if (dayInRange(day, asReconDay(batch.dateStart), asReconDay(batch.dateEnd))) next[day] = true;
    }
    setPicked(next);
    setEditing(batch.id);
    setMr080Amount(batch.mr080Amount == null ? '' : String(batch.mr080Amount));
    setBatchAmount(batch.batchAmount == null ? '' : String(batch.batchAmount));
    setDepositAmount(batch.depositAmount == null ? '' : String(batch.depositAmount));
    setSetNotes(batch.notes ?? '');
    setPendingFiles([]);
  }

  function resetSet(): void {
    setPicked({});
    setEditing(null);
    setMr080Amount('');
    setBatchAmount('');
    setDepositAmount('');
    setSetNotes('');
    setPendingFiles([]);
  }

  function parkSet(): void {
    setPicked({});
    setEditingId(null);
    setMr080Amount('');
    setBatchAmount('');
    setDepositAmount('');
    setSetNotes('');
    setPendingFiles([]);
  }

  async function retireBatch(id: number): Promise<void> {
    await run(async () => {
      try {
        await softDeleteMoneyRow('depositBatch', id);
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 404) throw err;
      }
      await refreshDepositRecon();
    }, resetSet, { freeze: false });
  }

  async function clearSet(): Promise<void> {
    resetSet();
  }

  async function saveDay(reconDate: string, field: DayField, value: string): Promise<void> {
    const stored = byDate.get(reconDate);
    const merged = mergeDay(stored, { ...(drafts[reconDate] ?? {}), [field]: value });
    setDrafts((cur) => ({ ...cur, [reconDate]: merged }));
    setDaySaveCount((c) => c + 1);
    try {
      await run(async () => {
        const row = await saveDepositRecon({
          locationCode,
          reconDate,
          mr080: merged.mr080,
          envelope: merged.envelope,
          overUnder: merged.overUnder,
          notes: merged.notes,
        });
        await upsertEntity('depositRecon', row.id, {
          ...stored,
          ...row,
        });
        // Reached only when the write above actually succeeded — a rejected
        // saveDepositRecon or upsertEntity above throws and run() catches it
        // into `error`, never reaching this line (idea #23).
        setSavedAt(new Date());
      }, undefined, { freeze: false });
    } finally {
      setDaySaveCount((c) => c - 1);
    }
  }

  /** Idea #23: every day box a manager typed into and left different from
   * what's currently saved, committed through the exact same saveDay() the
   * blur handler below calls — same payload shape, same endpoint, no new API. */
  function commitDay(
    day: string, field: DayField, value: string, next: Record<DayField, string>,
  ): Promise<void> {
    setDrafts((cur) => ({ ...cur, [day]: next }));
    setPendingByDay((cur) => {
      if (!(day in cur)) return cur;
      const rest = { ...cur };
      delete rest[day];
      return rest;
    });
    return saveDay(day, field, value);
  }

  function trackTyped(day: string, next: Record<DayField, string>): void {
    const baseline = mergeDay(byDate.get(day), drafts[day] ?? {});
    const dirty = DAY_FIELDS.some((f) => next[f] !== baseline[f]);
    setPendingByDay((cur) => {
      if (!dirty) {
        if (!(day in cur)) return cur;
        const rest = { ...cur };
        delete rest[day];
        return rest;
      }
      return { ...cur, [day]: next };
    });
  }

  async function saveAllPending(): Promise<void> {
    const entries = Object.entries(pendingByDay);
    await Promise.all(entries.map(([day, next]) => {
      const baseline = mergeDay(byDate.get(day), drafts[day] ?? {});
      const field = DAY_FIELDS.find((f) => next[f] !== baseline[f]) ?? DAY_FIELDS[0];
      return commitDay(day, field, next[field], next);
    }));
  }

  async function closeOrSave(close: boolean): Promise<void> {
    if (!range) return;
    const clash = batches.find((b) => (
      b.id !== editingId
      && asReconDay(b.dateStart) <= range.end
      && asReconDay(b.dateEnd) >= range.start
    ));
    await run(async () => {
      if (clash) {
        throw new ApiError('Those dates overlap another batch. Each batch starts the day after the last one ends.', 409);
      }
      const row = await saveDepositBatch({
        ...(editingId ? { id: editingId } : {}),
        locationCode,
        dateStart: range.start,
        dateEnd: range.end,
        mr080Amount,
        batchAmount,
        depositAmount,
        notes: setNotes,
        closed: close || Boolean(editingId),
      });
      editingIdRef.current = row.id;
      let files = batchFiles(((state.value.depositBatch ?? {}) as Record<string, DepositBatchRow>)[String(row.id)]);
      for (const file of pendingFiles) {
        // eslint-disable-next-line no-await-in-loop
        const uploaded = await uploadDepositBatchFile(row.id, 'receipt', file);
        files = [...files, { id: uploaded.id, url: uploaded.url, name: uploaded.name }];
      }
      await upsertEntity('depositBatch', row.id, {
        ...((state.value.depositBatch ?? {}) as Record<string, DepositBatchRow>)[String(row.id)],
        ...row,
        files,
        updatedAt: (row as DepositBatchRow).updatedAt ?? new Date().toISOString(),
      });
      parkSet();
    });
  }

  async function attachFiles(batchId: number, picked: File[]): Promise<void> {
    if (!picked.length) return;
    await run(async () => {
      const current = ((state.value.depositBatch ?? {}) as Record<string, DepositBatchRow>)[String(batchId)];
      let files = batchFiles(current);
      for (const file of picked) {
        // eslint-disable-next-line no-await-in-loop
        const uploaded = await uploadDepositBatchFile(batchId, 'receipt', file);
        files = [...files, { id: uploaded.id, url: uploaded.url, name: uploaded.name }];
      }
      await upsertEntity('depositBatch', batchId, {
        ...current,
        id: batchId,
        locationCode,
        files,
      });
    });
  }

  function takeFiles(list: FileList | null): File[] {
    return list ? Array.from(list) : [];
  }

  function onPickFiles(list: FileList | null): void {
    const picked = takeFiles(list);
    if (!picked.length) return;
    if (editingId) {
      void attachFiles(editingId, picked);
      return;
    }
    setPendingFiles((cur) => [...cur, ...picked]);
  }

  async function removeFile(batchId: number, fileId: number): Promise<void> {
    await run(async () => {
      await deleteDepositBatchFile(fileId);
      const current = ((state.value.depositBatch ?? {}) as Record<string, DepositBatchRow>)[String(batchId)];
      if (!current) return;
      await upsertEntity('depositBatch', batchId, {
        ...current,
        files: batchFiles(current).filter((f) => f.id !== fileId),
      });
    });
  }

  // Idea #23: what the Save button/status line show. `null` while nothing
  // has ever been saved this session and no write is in flight — the status
  // line then renders empty rather than claiming a save that hasn't happened.
  const daySaveStatus: { cls: 'good' | 'muted'; text: string } | null = daySaveCount > 0
    ? { cls: 'muted', text: 'Saving…' }
    : savedAt
      ? { cls: 'good', text: `Saved ${formatClinicClock(savedAt)}` }
      : null;
  const daysSaveDisabled = daySaveCount > 0 || Object.keys(pendingByDay).length === 0;

  return (
    <div>
      {error ? <p role="alert">{error}</p> : null}

      <div class="deposit-sticky" data-testid="deposit-sticky">
      <div class="deposit-ytd" data-testid="deposit-ytd">
        <div class="deposit-ytd-box" data-testid="deposit-ytd-deposits">
          <span>Year to date deposits</span>
          <strong>{ytdDeposits ? `$${ytdDeposits}` : '$0.00'}</strong>
        </div>
      </div>

      <div class="deposit-set" data-testid="deposit-set">
        <strong class="deposit-set-label">{editingId ? 'Edit this set' : 'This set'}</strong>
        <span class="meta" data-testid="deposit-set-range">
          {range
            ? `${formatReconDay(range.start)} – ${formatReconDay(range.end)}`
            : 'Tick the dates that belong together.'}
        </span>
        {range ? (
          <span class="meta">
            Daily envelope {dailyEnvelopeTotal || '—'}
            {' · '}
            Daily MR080 {dailyMr080Total || '—'}
          </span>
        ) : null}
        <label>
          MR080 amount
          <input
            type="text"
            aria-label="MR080 amount"
            data-testid="deposit-set-mr080"
            value={mr080Amount}
            onInput={(ev: Event) => setMr080Amount((ev.target as HTMLInputElement).value)}
          />
        </label>
        <label>
          Batch amount
          <input
            type="text"
            aria-label="Batch amount"
            data-testid="deposit-set-batch"
            value={batchAmount}
            onInput={(ev: Event) => setBatchAmount((ev.target as HTMLInputElement).value)}
          />
        </label>
        <label>
          Deposit amount
          <input
            type="text"
            aria-label="Deposit amount"
            data-testid="deposit-set-deposit"
            value={depositAmount}
            onInput={(ev: Event) => setDepositAmount((ev.target as HTMLInputElement).value)}
          />
        </label>
        <span data-testid="deposit-set-overunder">
          Over/Under {setOverUnder || '—'}
        </span>
        <label>
          Notes
          <input
            type="text"
            aria-label="Set notes"
            value={setNotes}
            onInput={(ev: Event) => setSetNotes((ev.target as HTMLInputElement).value)}
          />
        </label>
        <div class="deposit-set-actions">
          {editingId ? (
            <button type="button" class="btn" data-testid="deposit-set-save" disabled={busy || !range} onClick={() => void closeOrSave(true)}>
              Save
            </button>
          ) : (
            <button
              type="button"
              class="btn"
              data-testid="deposit-set-close"
              disabled={busy || !range || !mr080Amount.trim() || !batchAmount.trim()}
              onClick={() => void closeOrSave(true)}
            >
              Close
            </button>
          )}
          <button type="button" class="btn" data-testid="deposit-set-clear" onClick={() => void clearSet()}>
            Reset form
          </button>
        </div>
        <div class="deposit-set-files" data-testid="deposit-set-files">
          <BatchFiles
            testId="deposit-set-upload"
            files={batchFiles(editing)}
            pending={pendingFiles}
            busy={busy}
            onPick={onPickFiles}
            onRemoveSaved={(id) => (editingId ? removeFile(editingId, id) : Promise.resolve())}
            onRemovePending={(index) => setPendingFiles((cur) => cur.filter((_, i) => i !== index))}
          />
        </div>
      </div>
      </div>

      {/* Idea #23 (owner ruling 2026-09-14): a real Save button beside the
          save-on-blur every box already has — same commit path, just a
          second way to trigger it, plus a visible "it saved" stamp. */}
      <div class="deposit-set-actions" data-testid="deposit-days-toolbar">
        <button
          type="button"
          class="btn primary"
          data-testid="deposit-days-save"
          disabled={daysSaveDisabled}
          onClick={() => void saveAllPending()}
        >
          {daySaveCount > 0 ? 'Saving…' : 'Save'}
        </button>
        <span class={daySaveStatus?.cls} data-testid="deposit-save-status">
          {daySaveStatus?.text ?? ''}
        </span>
      </div>

      <div class="table-wrap deposit-recon-scroll" data-testid="deposit-recon-scroll">
        <table class="data deposit-recon" data-testid="deposit-recon-grid">
          <thead>
            <tr>
              <th>In this set</th>
              <th>Date</th>
              {DAY_FIELDS.map((field) => (
                <th key={field}>{DAY_LABEL[field]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {days.map((day) => {
              const stored = byDate.get(day);
              const draft = drafts[day] ?? {};
              const closed = batchForDay(day);
              const open = Boolean(picked[day]);
              const rowClass = [
                closed ? 'deposit-day-closed' : '',
                open ? 'deposit-day-open' : '',
              ].filter(Boolean).join(' ') || undefined;
              return (
                <tr
                  key={day}
                  data-testid={`deposit-recon-${day}`}
                  class={rowClass}
                >
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Include ${formatReconDay(day)} in this set`}
                      data-testid={`deposit-recon-pick-${day}`}
                      checked={open}
                      disabled={busy}
                      onChange={() => toggleDay(day)}
                    />
                  </td>
                  <td>
                    {formatReconDay(day)}
                    {closed ? <small class="meta"> Closed</small> : null}
                  </td>
                  {open ? (
                    <DayBoxes
                      day={day}
                      stored={stored}
                      draft={draft}
                      onCommit={(field, value, next) => void commitDay(day, field, value, next)}
                      onType={trackTyped}
                    />
                  ) : (
                    DAY_FIELDS.map((field) => (
                      <td key={field}>
                        <span class="deposit-day-preview">{draft[field] ?? cellText(stored, field)}</span>
                      </td>
                    ))
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div class="deposit-batches" data-testid="deposit-batches">
        <h3>Closed batches</h3>
        {batches.length === 0 ? (
          <p class="meta">Nothing closed yet. Tick dates above, enter the two totals, upload if you have a file, then Close.</p>
        ) : (
          <div class="table-wrap">
            <table data-testid="deposit-closed-table">
              <thead>
                <tr>
                  <th>Dates</th>
                  <th>Daily total</th>
                  <th>MR080</th>
                  <th>Batch</th>
                  <th>Deposit</th>
                  <th>Over/Under</th>
                  <th>Notes</th>
                  <th>Files</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((batch) => (
                  <tr
                    key={batch.id}
                    class={editingId === batch.id ? 'deposit-batch on' : undefined}
                    data-testid={`deposit-batch-${batch.id}`}
                    onClick={() => {
                      // N-098 (2026-09-10): a closed batch no longer opens for
                      // edit on a plain row click — only the "Edit this
                      // batch" button does that now (mirrors N-093's move of
                      // delete off an implicit click). An open batch (not
                      // reachable from the UI today; see the comment below)
                      // keeps its old click-to-open behaviour.
                      if (!batch.closedAt) loadBatch(batch);
                    }}
                  >
                    <td>
                      <div class="tk">{formatReconDay(batch.dateStart)} – {formatReconDay(batch.dateEnd)}</div>
                      {batch.closedAt ? (
                        confirmDeleteId === batch.id ? (
                          <div
                            class="deposit-batch-confirm"
                            data-testid={`deposit-batch-confirm-${batch.id}`}
                            onClick={(ev) => ev.stopPropagation()}
                          >
                            <p class="meta" data-testid={`deposit-batch-confirm-text-${batch.id}`}>
                              {`Delete ${clinicName(batch.locationCode)} ${formatReconDay(batch.dateStart)} – ${formatReconDay(batch.dateEnd)}, ${moneyLabel(batch.batchAmount)}? This can be undone only by Tomer.`}
                            </p>
                            <button
                              type="button"
                              class="btn"
                              data-testid={`deposit-batch-confirm-delete-${batch.id}`}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                setConfirmDeleteId(null);
                                void retireBatch(batch.id);
                              }}
                            >
                              Delete
                            </button>
                            <button
                              type="button"
                              class="btn"
                              data-testid={`deposit-batch-confirm-keep-${batch.id}`}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                setConfirmDeleteId(null);
                              }}
                            >
                              Keep
                            </button>
                          </div>
                        ) : (
                          <>
                            {/* N-098: same spot "Delete this batch" sits, same
                                button style, open to anyone who could open this
                                batch by clicking the row before today — editing
                                a closed batch carries no role restriction of its
                                own (src/money.js's upsertDepositBatch checks only
                                location access; OWNER/DIRECTOR-only is a delete-
                                only rule from N-093). */}
                            <button
                              type="button"
                              class="btn"
                              data-testid={`deposit-batch-edit-${batch.id}`}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                loadBatch(batch);
                              }}
                            >
                              Edit this batch
                            </button>
                            {canDeleteClosedBatch(role) ? (
                              <button
                                type="button"
                                class="btn"
                                data-testid={`deposit-batch-delete-${batch.id}`}
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  setConfirmDeleteId(batch.id);
                                }}
                              >
                                Delete this batch
                              </button>
                            ) : null}
                          </>
                        )
                      ) : (
                        // Not yet closed: unchanged from before N-093 — this
                        // grid always closes a batch on save, so this branch
                        // is not reachable from the UI today, but a manager
                        // acting on an open batch is not the bug being fixed.
                        <button
                          type="button"
                          class="btn"
                          data-testid={`deposit-batch-remove-${batch.id}`}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            void retireBatch(batch.id);
                          }}
                        >
                          Clear
                        </button>
                      )}
                    </td>
                    <td class="n">{moneyLabel(dailyTotalFor(batch.dateStart, batch.dateEnd))}</td>
                    <td class="n">{moneyLabel(batch.mr080Amount)}</td>
                    <td class="n">{moneyLabel(batch.batchAmount)}</td>
                    <td class="n">{moneyLabel(batch.depositAmount)}</td>
                    <td class="n">{moneyLabel(batch.overUnder)}</td>
                    <td>{batch.notes || '—'}</td>
                    <td onClick={(ev) => ev.stopPropagation()}>
                      <BatchFiles
                        files={batchFiles(batch)}
                        busy={busy}
                        onPick={(list) => void attachFiles(batch.id, takeFiles(list))}
                        onRemoveSaved={(id) => removeFile(batch.id, id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function BatchFiles({
  files, pending = [], busy, onPick, onRemoveSaved, onRemovePending, testId,
}: {
  files: DepositReconFile[];
  pending?: File[];
  busy: boolean;
  onPick: (list: FileList | null) => void;
  onRemoveSaved: (id: number) => Promise<void>;
  onRemovePending?: (index: number) => void;
  testId?: string;
}): JSX.Element {
  const email = session.value.me?.email ?? null;
  return (
    <div class="deposit-closed-file">
      {files.map((file) => (
        <span key={file.id} class="deposit-file-chip">
          {file.url ? (
            <a href={withGoogleAccount(file.url, email)} target="_blank" rel="noopener noreferrer">
              {file.name || 'File'}
            </a>
          ) : (
            <span>{file.name || 'File'}</span>
          )}
          <button type="button" disabled={busy} onClick={() => void onRemoveSaved(file.id)}>
            Remove
          </button>
        </span>
      ))}
      {pending.map((file, index) => (
        <span key={`pending-${file.name}-${index}`} class="deposit-file-chip">
          <span>{file.name}</span>
          {onRemovePending ? (
            <button type="button" disabled={busy} onClick={() => onRemovePending(index)}>
              Remove
            </button>
          ) : null}
        </span>
      ))}
      <label
        class="btn deposit-upload-btn"
        data-testid={testId ?? 'deposit-upload'}
        title={busy ? 'Uploading…' : 'Upload a file'}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M21.44 11.05 12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
          />
        </svg>
        {busy ? 'Uploading…' : 'Upload'}
        <input
          type="file"
          hidden
          multiple
          aria-label="Upload a file"
          disabled={busy}
          onChange={(ev: Event) => {
            const input = ev.target as HTMLInputElement;
            onPick(input.files);
            input.value = '';
          }}
        />
      </label>
    </div>
  );
}
